import { FormProvider } from 'react-hook-form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/view/components/ui/dialog'
import { Skeleton } from '@/view/components/ui/skeleton'
import { BasicSelect } from '@/view/components/BasicSelect'
import { BasicInput } from '@/view/components/BasicInput'
import { Button } from '@/view/components/ui/button'
import masks from '@/app/utils/masks'
import { plateMask } from '@/app/utils/plateFormater'
import useVehicleFormModal from './useVehicleFormModal'
import { ConfirmationTransactionModal } from '@/view/pages/WithdrawSimulation/components/NewWithdrawModal/components/ConfirmationTransactionModal'

interface VehicleFormModalProps {
  onBack: () => void
  onContinue: () => void
}

export default function VehicleFormModal({
  onBack,
  onContinue,
}: VehicleFormModalProps) {
  const {
    form,
    isLoading,
    stateOptions,
    isLoadingStateData,
    stateId,
    cityOptions,
    isLoadingCityData,
    itemsTypeVehicle,
    isLoadingTypeVehicleData,
    typeVehicle,
    isLoadingBrandData,
    brandOptions,
    vehicle,
    isLoadingYearData,
    yearOptions,
    year,
    isLoadingModelData,
    modelOptions,
    commisionData,
    isLoadingCommisionData,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    handleOpenConfirmModal,
    handleSendProposal,
    pin,
    setPin,
    user,
    plate,
    cityId,
  } = useVehicleFormModal({
    onContinue,
  })

  const {
    formState: { errors },
  } = form

  return (
    <DialogContent className="max-h-[740px] overflow-y-auto sm:max-w-3xl">
      <DialogHeader className="flex flex-row items-start justify-between border-b pb-8">
        <div>
          <DialogTitle className="text-xl font-semibold text-black01">
            Indicação Salvcar
          </DialogTitle>
          <DialogDescription className="!mt-0 text-base text-black04">
            Preencha os campos para gerar a proposta
          </DialogDescription>
        </div>
      </DialogHeader>

      <form onSubmit={(e) => e.preventDefault()}>
        <FormProvider {...form}>
          <div className="mt-2 flex flex-col gap-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="flex-1">
                <BasicInput
                  name="plate"
                  label="Placa"
                  error={errors.plate?.message}
                  onInput={plateMask}
                  maxLength={8}
                />
              </div>
              <div className="flex flex-1 justify-end">
                {isLoadingCommisionData ? (
                  <Skeleton className="h-8 w-60" />
                ) : (
                  commisionData && (
                    <div className="flex flex-col space-y-1 text-right">
                      <div className="ml-auto w-max rounded-full bg-primary px-3 py-1 shadow-lg">
                        <p className="text-sm font-semibold text-white">
                          {masks.currency(
                            Number(commisionData?.range.from ?? 0) * 100,
                          )}
                          {' - '}
                          {masks.currency(
                            Number(commisionData?.range.to ?? 0) * 100,
                          )}
                        </p>
                      </div>
                      <p className="max-w-[16rem] text-xs text-black04">
                        O valor de comissão está sujeito a mudança pós validação
                        dos dados.
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 md:flex-row">
              <BasicSelect
                name="stateId"
                label="Estado"
                options={stateOptions}
                placeholder="Selecione uma opção"
                error={errors.stateId?.message}
                disabled={!plate || isLoadingStateData}
              />
              <BasicSelect
                name="cityId"
                label="Cidade"
                options={cityOptions}
                placeholder="Selecione uma opção"
                error={errors.cityId?.message}
                disabled={!stateId || isLoadingCityData}
              />
            </div>

            <div className="flex flex-col gap-4 md:flex-row">
              <BasicSelect
                name="typeVehicle"
                label="Tipos de veículos"
                options={itemsTypeVehicle}
                placeholder="Selecione uma opção"
                error={errors.typeVehicle?.message}
                disabled={!cityId || isLoadingTypeVehicleData}
              />
              <BasicSelect
                name="vehicle"
                label="Marca"
                options={brandOptions}
                placeholder="Selecione uma opção"
                error={errors.vehicle?.message}
                disabled={!typeVehicle || isLoadingBrandData}
              />
              <BasicSelect
                name="year"
                label="Ano do veículo"
                options={yearOptions}
                placeholder="Selecione uma opção"
                error={errors.year?.message}
                disabled={!vehicle || isLoadingYearData}
              />
            </div>

            <div className="flex flex-col gap-4 md:flex-row">
              <BasicSelect
                name="modelId"
                label="Modelo do veículo"
                options={modelOptions}
                placeholder="Selecione uma opção"
                error={errors.modelId?.message}
                disabled={!vehicle || !year || isLoadingModelData}
              />
            </div>

            <div className="flex flex-col gap-4 md:flex-row">
              <BasicInput
                name="description"
                label="Descrição"
                error={errors.description?.message}
                maxLength={50}
                className="flex-1"
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-[160px] rounded-full text-primary"
              onClick={onBack}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              className="w-[160px] rounded-full font-semibold text-white"
              isLoading={isLoading}
              onClick={handleOpenConfirmModal}
            >
              Continuar
            </Button>
          </div>
        </FormProvider>
      </form>

      <Dialog open={isConfirmModalOpen} onOpenChange={setIsConfirmModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar indicação?</DialogTitle>
            <DialogDescription>
              <div className="mt-4">
                Ao confirmar você afirma que seu indicado está ciente desta
                indicação, caso não esteja seu acesso pode ser bloqueado.
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              className="w-[120px] rounded-full text-primary"
              onClick={() => setIsConfirmModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              className="w-[120px] rounded-full font-semibold text-white"
              isLoading={isLoading}
              onClick={handleSendProposal}
            >
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationTransactionModal
        clientName={form.watch('plate') || 'Cliente'}
        isOpen={isConfirmModalOpen}
        onClose={setIsConfirmModalOpen}
        pin={pin}
        setPin={setPin}
        submitForm={handleSendProposal}
        user={user}
      />
    </DialogContent>
  )
}
